import { useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Eye, EyeOff, KeyRound, Link2, Cpu, ScanText, Clock, FlaskConical, Loader2 } from 'lucide-react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfigStore } from '@/store/useConfigStore';
import { Button } from '@/components/ui/Button';
import { Input, Switch } from '@/components/ui/Input';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Tag';
import { generateId } from '@/lib/storage';
import { cn } from '@/lib/utils';
import type { OCRInstance } from '@/types';
import { callOCR, isLocalOCRInstance } from '@/services/ocrService';

const OCR_TEST_IMAGE_URL = 'https://paddle-model-ecology.bj.bcebos.com/paddlex/imgs/demo_image/general_ocr_002.png';

interface FormState {
    name: string;
    type: string;
    apiUrl: string;
    token: string;
    model: string;
    enabled: boolean;
}

const emptyForm: FormState = {
    name: '',
    type: 'PaddleOCR',
    apiUrl: '',
    token: '',
    model: '',
    enabled: true,
};

export function OCRConfigPage({ embedded = false }: { embedded?: boolean } = {}) {
    const {
        ocrInstances,
        ocrSettings,
        addOCRInstance,
        updateOCRInstance,
        deleteOCRInstance,
        reorderOCRInstances,
        updateOCRSettings,
    } = useConfigStore();

    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const openAdd = () => {
        setForm(emptyForm);
        setEditingId(null);
        setFormOpen(true);
    };

    const openEdit = (inst: OCRInstance) => {
        setForm({
            name: typeof inst.name === 'string' ? inst.name : '',
            type: typeof inst.type === 'string' ? inst.type : 'PaddleOCR',
            apiUrl: typeof inst.apiUrl === 'string' ? inst.apiUrl : '',
            token: typeof inst.token === 'string' ? inst.token : '',
            model: typeof inst.model === 'string' ? inst.model : '',
            enabled: inst.enabled,
        });
        setEditingId(inst.id);
        setFormOpen(true);
    };

    const submit = () => {
        const name = form.name.trim();
        const apiUrl = form.apiUrl.trim();
        if (!name || !apiUrl) return;
        const data = {
            name,
            type: form.type.trim() || 'PaddleOCR',
            apiUrl,
            token: form.token.trim(),
            model: form.model.trim(),
            enabled: form.enabled,
        };
        if (editingId) {
            updateOCRInstance(editingId, data);
        } else {
            addOCRInstance({ id: generateId(), usedCount: 0, quota: 0, ...data });
        }
        setFormOpen(false);
    };

    const confirmDelete = () => {
        if (deleteId) deleteOCRInstance(deleteId);
        setDeleteId(null);
    };

    const handleDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIndex = ocrInstances.findIndex(i => i.id === active.id);
        const newIndex = ocrInstances.findIndex(i => i.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        reorderOCRInstances(arrayMove(ocrInstances, oldIndex, newIndex));
    };

    return (
        <div className="flex h-full flex-col">
            {!embedded && <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">OCR 实例管理</h1>
                    <p className="text-xs text-zinc-500">管理 OCR 识别接口，拖拽卡片可调整优先级</p>
                </div>
                <Button variant="primary" size="sm" onClick={openAdd}>
                    <Plus size={14} />
                    添加实例
                </Button>
            </div>}
            {embedded && <div className="flex shrink-0 justify-end px-4 pt-3"><Button variant="primary" size="sm" onClick={openAdd}><Plus size={14} />添加实例</Button></div>}

            <div className="flex-1 overflow-auto p-4">
                <div className="mx-auto flex max-w-3xl flex-col gap-4">
                    {/* 请求间隔配置 */}
                    <section className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center gap-3">
                            <Clock size={16} className="shrink-0 text-zinc-400" />
                            <div className="min-w-0 flex-1">
                                <h2 className="text-sm font-semibold text-zinc-900">请求间隔</h2>
                                <p className="text-xs text-zinc-500">批量 OCR 调用时每次请求之间的间隔时间</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min={0}
                                    value={ocrSettings.requestInterval}
                                    onChange={(e) => {
                                        const v = Number(e.target.value);
                                        updateOCRSettings({ requestInterval: v >= 0 ? v : 0 });
                                    }}
                                    className="w-24"
                                />
                                <span className="text-sm text-zinc-500">秒</span>
                            </div>
                        </div>
                    </section>

                    {ocrInstances.length === 0 ? (
                        <EmptyState onAdd={openAdd} />
                    ) : (
                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext
                                items={ocrInstances.map(i => i.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="flex flex-col gap-3">
                                    {ocrInstances.map(inst => (
                                        <SortableCard
                                            key={inst.id}
                                            instance={inst}
                                            onEdit={() => openEdit(inst)}
                                            onDelete={() => setDeleteId(inst.id)}
                                            onToggleEnabled={(v) => updateOCRInstance(inst.id, { enabled: v })}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>
            </div>

            <Modal
                open={formOpen}
                onClose={() => setFormOpen(false)}
                title={editingId ? '编辑实例' : '添加实例'}
                width="md"
            >
                <OCRInstanceForm
                    form={form}
                    onChange={setForm}
                    onSubmit={submit}
                    onCancel={() => setFormOpen(false)}
                />
            </Modal>

            <ConfirmDialog
                open={!!deleteId}
                title="删除实例"
                message="确定要删除该 OCR 实例吗？此操作不可撤销。"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteId(null)}
            />
        </div>
    );
}

function SortableCard({
    instance,
    onEdit,
    onDelete,
    onToggleEnabled,
}: {
    instance: OCRInstance;
    onEdit: () => void;
    onDelete: () => void;
    onToggleEnabled: (v: boolean) => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: instance.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const token = typeof instance.token === 'string' ? instance.token : '';
    const isLocal = isLocalOCRInstance(instance);
    const [testing, setTesting] = useState(false);
    const [testMessage, setTestMessage] = useState('');
    const maskedToken = token
        ? token.length > 4
            ? `••••••••${token.slice(-4)}`
            : '••••••••'
        : '未设置';

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'rounded-lg border border-zinc-200 bg-white p-4 shadow-sm',
                isDragging && 'opacity-70 shadow-md ring-2 ring-zinc-300',
            )}
        >
            <div className="flex items-start gap-3">
                <button
                    {...attributes}
                    {...listeners}
                    className="mt-0.5 cursor-grab touch-none rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 active:cursor-grabbing"
                    title="拖拽排序"
                >
                    <GripVertical size={18} />
                </button>

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-semibold text-zinc-900">{instance.name}</h3>
                        <Badge variant="default">{instance.type}</Badge>
                        {instance.enabled ? (
                            <Badge variant="success">已启用</Badge>
                        ) : (
                            <Badge variant="error">已禁用</Badge>
                        )}
                    </div>
                    <div className="mt-2 flex flex-col gap-1.5 text-xs text-zinc-500">
                        {isLocal ? <div className="flex items-center gap-1.5"><Cpu size={13} className="shrink-0 text-zinc-400" /><span>浏览器本地中英文识别，无需 Token</span></div> : <>
                        <div className="flex items-center gap-1.5">
                            <Link2 size={13} className="shrink-0 text-zinc-400" />
                            <span className="truncate" title={instance.apiUrl}>{instance.apiUrl}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <KeyRound size={13} className="shrink-0 text-zinc-400" />
                            <span className="font-mono">{maskedToken}</span>
                        </div>
                        </>}
                        <div className="flex items-center gap-1.5">
                            <Cpu size={13} className="shrink-0 text-zinc-400" />
                            <span className="truncate">{instance.model || '—'}</span>
                        </div>
                    </div>
                    {testMessage && <p className={`mt-2 text-xs ${testMessage.startsWith('连接成功') ? 'text-emerald-600' : 'text-red-600'}`}>{testMessage}</p>}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                    <Switch checked={instance.enabled} onChange={onToggleEnabled} />
                    <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" disabled={testing || (!isLocal && (!token.trim() || !instance.model))} onClick={async () => {
                            setTesting(true);
                            setTestMessage('');
                            try {
                                const result = await callOCR(instance, OCR_TEST_IMAGE_URL, { trackUsage: false });
                                setTestMessage(result.success ? `连接成功 · ${result.instanceName ?? instance.name}` : (result.error || '识别失败'));
                            } catch (error) {
                                setTestMessage(error instanceof Error ? error.message : '识别失败');
                            } finally { setTesting(false); }
                        }}>
                            {testing ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}测试
                        </Button>
                        {!isLocal && <Button variant="ghost" size="sm" onClick={onEdit}>
                            <Pencil size={14} />
                            编辑
                        </Button>}
                        {!isLocal && <Button
                            variant="ghost"
                            size="sm"
                            onClick={onDelete}
                            className="text-red-500 hover:bg-red-50 hover:text-red-600"
                        >
                            <Trash2 size={14} />
                            删除
                        </Button>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function OCRInstanceForm({
    form,
    onChange,
    onSubmit,
    onCancel,
}: {
    form: FormState;
    onChange: (f: FormState) => void;
    onSubmit: () => void;
    onCancel: () => void;
}) {
    const [showToken, setShowToken] = useState(false);
    const update = (patch: Partial<FormState>) => onChange({ ...form, ...patch });
    const valid = form.name.trim() !== '' && form.apiUrl.trim() !== '';

    return (
        <div className="flex flex-col gap-4">
            <Input
                label="实例名称"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="如：PaddleOCR 主实例"
            />
            <div className="grid grid-cols-2 gap-3">
                <Input
                    label="类型"
                    value={form.type}
                    onChange={(e) => update({ type: e.target.value })}
                    placeholder="PaddleOCR"
                />
                <Input
                    label="模型"
                    value={form.model}
                    onChange={(e) => update({ model: e.target.value })}
                    placeholder="PaddleOCR-VL-1.5"
                />
            </div>
            <Input
                label="API URL"
                value={form.apiUrl}
                onChange={(e) => update({ apiUrl: e.target.value })}
                placeholder="https://paddleocr.aistudio-app.com/api/v2/ocr/jobs"
            />
            <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-zinc-600">Token</label>
                <div className="relative">
                    <input
                        type={showToken ? 'text' : 'password'}
                        value={form.token}
                        onChange={(e) => update({ token: e.target.value })}
                        placeholder="Bearer Token"
                        className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 pr-9 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    />
                    <button
                        type="button"
                        onClick={() => setShowToken(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                        title={showToken ? '隐藏' : '显示'}
                    >
                        {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
                <span className="text-sm text-zinc-700">启用此实例</span>
                <Switch checked={form.enabled} onChange={(v) => update({ enabled: v })} />
            </div>
            <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onCancel}>取消</Button>
                <Button variant="primary" size="sm" onClick={onSubmit} disabled={!valid}>
                    保存
                </Button>
            </div>
        </div>
    );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-zinc-200 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-100 text-zinc-400">
                <ScanText size={28} />
            </div>
            <div>
                <p className="text-sm font-medium text-zinc-700">暂无 OCR 实例</p>
                <p className="text-xs text-zinc-400">添加一个实例以开始使用低质量过滤、尺码图识别等功能</p>
            </div>
            <Button variant="primary" size="sm" onClick={onAdd}>
                <Plus size={14} />
                添加实例
            </Button>
        </div>
    );
}
